import {
  AccessControlRequestStatus,
  AclVerb,
} from '@agilelab/plugin-wb-access-control-common';
import { AccessControlResponseNotification } from '@agilelab/plugin-wb-notification-common';
import { parseEntityRef } from '@mainstage/catalog-model';
import { WbAvatar, WbButton, WbLink } from '@agilelab/plugin-wb-platform';
import {
  capitalize,
  Chip,
  IconButton,
  Typography,
  useTheme,
} from '@material-ui/core';
import CheckCircleOutlineOutlinedIcon from '@material-ui/icons/CheckCircleOutlineOutlined';
import ErrorOutlineOutlinedIcon from '@material-ui/icons/ErrorOutlineOutlined';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import NotInterestedIcon from '@material-ui/icons/NotInterested';
import { DateTime } from 'luxon';
import { useCallback, useId, useState } from 'react';
import { AccessControlDisplayFields } from './AccessControlDisplayFields';
import { ConsumableOutcomesDrawer } from './ConsumableOutcomesDrawer';
import { MarkAsReadButton } from './MarkAsReadButton';
import {
  NotificationDisclosure,
  NotificationDisclosureDetails,
} from './NotificationDisclosure';
import { useNotificationStyle } from './NotificationsPageStyle';
import logo from './witboost_logo.svg';

const composeMessage = (
  verb: AclVerb,
  outcomes: AccessControlResponseNotification['notification_request']['consumableOutcomes'],
  status: 'SUCCESS' | 'FAILURE' | 'REJECTED',
  isRequester: boolean,
) => {
  const partiallyCompleted =
    status === 'FAILURE' &&
    outcomes?.some(item => item.outcome === 'completed');
  if (partiallyCompleted) {
    return isRequester
      ? `The following access ${verb} could not be completed due to an error. See ${verb} access details for each consumable's status and try again or contact the resource owner.`
      : `The following access ${verb} could not be completed due to an error. See ${verb} access details for each consumable's status and try again or contact the platform team.`;
  } else if (status === 'FAILURE') {
    return isRequester
      ? `The following access ${verb} could not be completed due to an error. Please try again or contact the resource owner.`
      : `The following access ${verb} could not be completed due to an error. Please try again or contact the platform team.`;
  } else if (status === 'REJECTED') {
    return `The following ${verb} request has been rejected.`;
  }

  return isRequester
    ? 'The Access Control List for the following resource has been successfully updated as requested.'
    : 'The Access Control List for the following resource has been updated.';
};

const buildLinkToUser = (userOrGroupRef: string): string => {
  const parsedRef = parseEntityRef(userOrGroupRef);

  return `/catalog/default/${parsedRef.kind}/${parsedRef.name}`;
};

const parseName = (userOrGroupRef: string): string => {
  return parseEntityRef(userOrGroupRef).name;
};

export const AccessControlResponseNotificationDisclosure = (props: {
  verb: AclVerb;
  notification: AccessControlResponseNotification;
  onAccordionExpansionsChange: (value: string | boolean) => void;
  expandedAccordion: string | boolean;
}) => {
  const { verb, notification, onAccordionExpansionsChange, expandedAccordion } =
    props;
  const classes = useNotificationStyle();
  const theme = useTheme();
  const detailsRegionId = useId();
  const [outcomesOpen, setOutcomesOpen] = useState(false);
  const outcomes = notification.notification_request.consumableOutcomes;
  const requestStatus = notification.notification_request.status;
  const showRequestDetails =
    (requestStatus === 'SUCCESS' || requestStatus === 'FAILURE') &&
    !!outcomes?.length;

  const isExpanded = expandedAccordion === notification.id.toString();

  const onToggle = useCallback(() => {
    onAccordionExpansionsChange(
      isExpanded ? false : notification.id.toString(),
    );
  }, [isExpanded, notification.id, onAccordionExpansionsChange]);

  const notificationHeader = (
    <div className={classes.expandableSummaryHeaderOutsideDisclosure}>
      <Typography className={classes.notificationTime}>
        {DateTime.fromISO(notification.created_at).toFormat('dd/MM/yyyy HH:mm')}
      </Typography>
      <div className={classes.expandableSummaryHeaderActions}>
        <MarkAsReadButton notification={notification} />
        <IconButton
          onClick={onToggle}
          size="small"
          aria-expanded={isExpanded}
          aria-controls={detailsRegionId}
          aria-label="toggle notification details"
          className={`${classes.disclosureToggleButton} ${
            isExpanded ? classes.disclosureToggleButtonExpanded : ''
          }`}
        >
          <ExpandMoreIcon color="primary" />
        </IconButton>
      </div>
    </div>
  );

  return (
    <div className={classes.expandableDisclosureContainer}>
      {notificationHeader}
      <NotificationDisclosure
        expanded={isExpanded}
        style={{
          border: 'none',
          borderBottom: 'none',
          backgroundColor: !!notification.read_at
            ? 'transparent'
            : theme.palette.common.white,
        }}
      >
        <div className={classes.expandableSummaryBody}>
          <div className={classes.expandableSummaryBodyContent}>
            <div className={classes.summaryDescription}>
              <div style={{ display: 'flex', alignItems: 'center' }}>
                {!notification.notification_request.status && (
                  <ErrorOutlineOutlinedIcon color="primary" />
                )}
                {notification.notification_request.status === 'SUCCESS' && (
                  <CheckCircleOutlineOutlinedIcon
                    style={{ color: theme.palette.success.main }}
                  />
                )}
                {(notification.notification_request.status === 'FAILURE' ||
                  notification.notification_request.status === 'REJECTED') && (
                  <NotInterestedIcon
                    style={{ color: theme.palette.error.main }}
                  />
                )}
                <WbAvatar
                  displayName={notification.sender || 'witboost'}
                  picture={logo}
                  classes={{ avatar: classes.avatar }}
                />
              </div>
              <div className={classes.summaryDescriptionText}>
                <span>
                  {composeMessage(
                    verb,
                    outcomes,
                    notification.notification_request.status,
                    notification.notification_request.is_requester,
                  )}
                </span>
                <span>
                  <b>action:</b>{' '}
                  <Typography color="primary" component="b">
                    {verb}
                  </Typography>
                </span>
                <span>
                  <b>resource:</b>{' '}
                  <WbLink
                    to={`/marketplace/search/${notification.notification_request.id_dataproduct_instance}`}
                  >
                    <Typography color="primary" component="b">
                      {
                        notification.notification_request.dataproduct
                          .display_name
                      }
                    </Typography>
                  </WbLink>{' '}
                </span>
                {notification.notification_request.is_requester && (
                  <span>
                    {' '}
                    <b>owner:</b>{' '}
                    <WbLink
                      to={buildLinkToUser(
                        notification.notification_request.dataproduct
                          .data_product_owner,
                      )}
                    >
                      <Typography color="primary" component="b">
                        {parseName(
                          notification.notification_request.dataproduct
                            .data_product_owner,
                        )}
                      </Typography>
                    </WbLink>{' '}
                  </span>
                )}
                <span>
                  <b>domain:</b>{' '}
                  <Typography color="primary" component="b">
                    {notification.notification_request.dataproduct.domain}
                  </Typography>
                </span>
                <span>
                  <b>environment:</b>{' '}
                  <Typography color="primary" component="b">
                    {notification.notification_request.environment}
                  </Typography>
                </span>
                <span>
                  <b>version:</b>{' '}
                  <Typography color="primary" component="b">
                    {notification.notification_request.dataproduct.version}
                  </Typography>
                </span>
              </div>
            </div>
          </div>
        </div>
        <NotificationDisclosureDetails id={detailsRegionId}>
          <div>
            <div className={classes.contentElementHeader}>
              <Typography
                variant="h6"
                color="primary"
                style={{ marginBottom: theme.spacing(1) }}
                paragraph
              >
                Consumable
              </Typography>
              <Typography variant="body1">
                <b>Name:</b> {notification.notification_request.outputport.name}
              </Typography>
              <Typography variant="body1">
                <b>Type:</b>{' '}
                {notification.notification_request.outputport.output_port_type}
              </Typography>
            </div>
            <div className={classes.contentElementHeader}>
              <Typography
                variant="h6"
                color="primary"
                style={{ marginBottom: theme.spacing(1) }}
                paragraph
              >
                Requested for
              </Typography>
              <div
                style={{
                  display: 'flex',
                  flexWrap: 'wrap',
                  gap: theme.spacing(1),
                }}
              >
                {notification.notification_request.identities &&
                  notification.notification_request.identities.map(i => (
                    <Chip title={i} key={i} label={i} size="small" />
                  ))}
              </div>
            </div>
            {notification.notification_request.is_requester ? (
              <></>
            ) : (
              <div className={classes.contentElementHeader}>
                <Typography
                  variant="h6"
                  color="primary"
                  style={{ marginBottom: theme.spacing(1) }}
                  paragraph
                >
                  Requester
                </Typography>
                <Typography variant="body1">
                  <b>Name:</b>{' '}
                  {notification.notification_request.requester_display_name}{' '}
                  <Chip
                    title={notification.notification_request.requester}
                    key={notification.notification_request.requester}
                    label={notification.notification_request.requester}
                    size="small"
                  />
                </Typography>
                {notification.notification_request.motivation && (
                  <Typography variant="body1">
                    <b>Motivation:</b>{' '}
                    {notification.notification_request.motivation}
                  </Typography>
                )}
              </div>
            )}
            <AccessControlDisplayFields
              notification={notification.notification_request}
            />
            {notification.notification_response?.status ===
              AccessControlRequestStatus.ACCEPT && (
              <Typography style={{ color: theme.palette.success.main }}>
                Request accepted on:{' '}
                {DateTime.fromISO(
                  notification.notification_response?.timestamp,
                ).toFormat('dd/MM/yyyy HH:mm')}
              </Typography>
            )}
            {notification.notification_response?.status ===
              AccessControlRequestStatus.REJECT && (
              <Typography style={{ color: theme.palette.error.main }}>
                Access rejected :{' '}
                {DateTime.fromISO(
                  notification.notification_response?.timestamp,
                ).toFormat('dd/MM/yyyy HH:mm')}
              </Typography>
            )}
            {notification.notification_request?.status === 'FAILURE' && (
              <Typography style={{ color: theme.palette.error.main }}>
                Access request failed on:{' '}
                {DateTime.fromISO(notification.updated_at).toFormat(
                  'dd/MM/yyyy HH:mm',
                )}
              </Typography>
            )}
            {showRequestDetails && (
              <WbButton
                style={{ marginRight: theme.spacing(1) }}
                variant="outlined"
                color="primary"
                label={`${capitalize(verb)} access details`}
                onClick={() => setOutcomesOpen(true)}
              />
            )}
          </div>
        </NotificationDisclosureDetails>
      </NotificationDisclosure>
      {showRequestDetails &&
        outcomes &&
        !notification.notification_request.is_requester && (
          <ConsumableOutcomesDrawer
            open={outcomesOpen}
            setOpen={setOutcomesOpen}
            outcomes={outcomes}
            verb={verb}
          />
        )}
    </div>
  );
};
